import { create } from 'zustand';

type ChecklistHintGate = {
  shareDone: boolean;
};

type GuidanceUiState = {
  /** Bumps when user asks to replay orientation (Help → Watch intro). */
  replayNonce: number;
  requestOrientationReplay: () => void;
  /** Bumps when Help/More asks to reopen a dismissed get-started checklist. */
  checklistRestoreNonce: number;
  requestChecklistRestore: () => void;
  /** Bumps when the user shares / opens their public profile. */
  getStartedShareSignal: number;
  signalGetStartedShare: () => void;
  isTourActive: boolean;
  setTourActive: (active: boolean) => void;
  /**
   * The real „Loslegen“ launcher is on screen (not the invisible
   * placeholder). The orientation tour only ends on it when true (DEV-574).
   */
  isChecklistLauncherVisible: boolean;
  setChecklistLauncherVisible: (visible: boolean) => void;
  /** Soft gate: delay interrupting announcements while get-started panel is open. */
  isChecklistPanelOpen: boolean;
  setChecklistPanelOpen: (open: boolean) => void;
  /**
   * Wizard footers occupy the same bottom-right corner as the get-started
   * launcher. Hide the launcher while a wizard CTA is on screen.
   */
  isWizardFooterActive: boolean;
  setWizardFooterActive: (active: boolean) => void;
  /** Checklist gates for hints (avoids requiring ChecklistProvider). */
  checklistHintGate: ChecklistHintGate;
  setChecklistHintGate: (gate: ChecklistHintGate) => void;
  /**
   * Hint IDs dismissed this session (X, visit, etc.).
   * Survives TourKit unregistering a hint when its HintHeadless unmounts —
   * otherwise discovery tips like nav-products reappear after leaving the page.
   */
  dismissedHintIds: ReadonlySet<string>;
  markHintDismissed: (hintId: string) => void;
};

export const useGuidanceStore = create<GuidanceUiState>((set) => ({
  replayNonce: 0,
  requestOrientationReplay: () =>
    set((state) => ({ replayNonce: state.replayNonce + 1 })),
  checklistRestoreNonce: 0,
  requestChecklistRestore: () =>
    set((state) => ({
      checklistRestoreNonce: state.checklistRestoreNonce + 1,
    })),
  getStartedShareSignal: 0,
  signalGetStartedShare: () =>
    set((state) => ({
      getStartedShareSignal: state.getStartedShareSignal + 1,
    })),
  isTourActive: false,
  setTourActive: (active) => set({ isTourActive: active }),
  isChecklistLauncherVisible: false,
  setChecklistLauncherVisible: (visible) =>
    set({ isChecklistLauncherVisible: visible }),
  isChecklistPanelOpen: false,
  setChecklistPanelOpen: (open) => set({ isChecklistPanelOpen: open }),
  isWizardFooterActive: false,
  setWizardFooterActive: (active) => set({ isWizardFooterActive: active }),
  checklistHintGate: {
    shareDone: false,
  },
  setChecklistHintGate: (gate) => set({ checklistHintGate: gate }),
  dismissedHintIds: new Set(),
  markHintDismissed: (hintId) =>
    set((state) => {
      if (state.dismissedHintIds.has(hintId)) return state;
      const next = new Set(state.dismissedHintIds);
      next.add(hintId);
      return { dismissedHintIds: next };
    }),
}));
